/**
 * YarRoom: the module yar's conversation, drawn with the smart assistant's
 * pieces (message stream, «در حال تحلیل» card, cost confirmation, answer card,
 * error card, composer) so a yar waits, confirms and answers exactly like
 * «دستیار هوشمند». Talks to the quote/run API: a question is quoted when sent,
 * reserved and run only after «اجرا», and remembered in the personal history.
 */
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { Icon, V2Button } from '@sakhtar/design-system/react';
import { AiConnectProvider, useAiProvider } from '../../routes/ai/connect/AiProviderContext.js';
import { AiAbortedError, AiContractError, AiTimeoutError } from '../../routes/ai/connect/errors.ts';
import { fetchTransport } from '../../routes/ai/connect/fetchTransport.js';
import { isAiLive } from '../../routes/ai/connect/mode.ts';
import type { AiDomain, AiTool } from '../../routes/ai/connect/contract.ts';
import { AnswerView } from '../../routes/ai/chat/blocks/AnswerView.js';
import { TextBlock } from '../../routes/ai/chat/blocks/TextBlock.js';
import { scrollWithin } from '../../routes/ai/chat/MessageStream.js';
import { AnalyzingCard, ConfirmHeavyCard, StoppedNote } from '../../routes/ai/chat/states/index.js';
import type {
  AnalyzingMessage,
  AnswerMessage,
  ConfirmHeavyMessage,
} from '../../routes/ai/chat/types.ts';
import '../../routes/ai/chat/chat.css';
import '../../routes/ai/chat/states.css';
import { apiDelete, apiGet, apiPost } from '../../lib/apiClient.js';
import { useYarAccess } from './YarAccess.js';
import { scopeOf, yarAnswer, yarNotice } from './yarAnswer.ts';
import type { YarKey } from './yarConfig.ts';

export interface YarRoomProps {
  readonly assistantKey: YarKey;
  readonly tool: AiTool;
  readonly domain: AiDomain;
  readonly yarName: string;
  /** The character's picture for the empty conversation. */
  readonly avatar?: string;
  readonly placeholder?: string;
  /** Ready questions for an empty conversation; picking one asks it (a quote, nothing charged). */
  readonly suggestions?: readonly string[];
  /** Pre-filled composer text, e.g. a question picked on the module hub. */
  readonly initialQuestion?: string;
}

/** One entry of this session's stream, in the smart assistant's message kinds. */
type Item =
  | { readonly id: string; readonly kind: 'user'; readonly text: string }
  | { readonly id: string; readonly kind: 'analyzing'; readonly stage: 'quote' | 'run' }
  | { readonly id: string; readonly kind: 'stopped' }
  | {
      readonly id: string;
      readonly kind: 'confirm';
      readonly question: string;
      readonly message: ConfirmHeavyMessage;
    }
  | { readonly id: string; readonly kind: 'answer'; readonly message: AnswerMessage }
  | {
      readonly id: string;
      readonly kind: 'error';
      readonly text: string;
      readonly question: string;
    };

type WithoutId<T> = T extends unknown ? Omit<T, 'id'> : never;
/** What a waiting card turns into. */
type Settled = WithoutId<Exclude<Item, { kind: 'user' | 'analyzing' }>>;

function failureText(error: unknown): string {
  if (error instanceof AiContractError) return error.displayMessage;
  // A lost response to a run says nothing about the server: it may have run and settled.
  if (
    (error instanceof AiTimeoutError || error instanceof AiAbortedError) &&
    error.outcomeUnknown
  ) {
    return 'پاسخی از سرور نرسید و ممکن است اجرا انجام شده باشد. مصرف و مانده را در «دستیار هوشمند ← کیف پول» ببینید.';
  }
  return 'تحلیل انجام نشد. هزینه‌ای ثبت نشده است.';
}

const NO_OP = () => undefined;

export function YarRoom({
  assistantKey,
  tool,
  domain,
  yarName,
  avatar,
  placeholder,
  suggestions,
  initialQuestion,
}: YarRoomProps) {
  const { slug } = useYarAccess();
  if (!isAiLive()) {
    return (
      <YarRoomShell
        disabled
        note={`این اتاق در این نسخهٔ برنامه به سرویس ${yarName} وصل نیست.`}
        yarName={yarName}
        {...(placeholder !== undefined ? { placeholder } : {})}
      >
        <YarEmpty yarName={yarName} {...(avatar !== undefined ? { avatar } : {})} />
      </YarRoomShell>
    );
  }
  return (
    <AiConnectProvider config={{ mode: 'live', slug, transport: fetchTransport }}>
      <LiveRoom
        assistantKey={assistantKey}
        tool={tool}
        domain={domain}
        yarName={yarName}
        {...(avatar !== undefined ? { avatar } : {})}
        {...(placeholder !== undefined ? { placeholder } : {})}
        {...(suggestions !== undefined ? { suggestions } : {})}
        {...(initialQuestion !== undefined ? { initialQuestion } : {})}
      />
    </AiConnectProvider>
  );
}

interface StoredMessage {
  readonly messageId: string;
  readonly role: 'user' | 'assistant';
  readonly body: string | null;
}

function LiveRoom({
  assistantKey,
  tool,
  domain,
  yarName,
  avatar,
  placeholder,
  suggestions,
  initialQuestion,
}: YarRoomProps) {
  const { slug } = useYarAccess();
  const provider = useAiProvider();
  const [text, setText] = useState(initialQuestion ?? '');
  const [items, setItems] = useState<readonly Item[]>([]);
  const [history, setHistory] = useState<readonly StoredMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  const [forgetNote, setForgetNote] = useState<string | null>(null);
  const counter = useRef(0);
  /** Cards the user stopped while quoting: their quote must not run on its own. */
  const stopped = useRef(new Set<string>());
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const busy = items.some((item) => item.kind === 'analyzing');
  const base = `/t/${slug}/api/v1/ai`;
  const scope = scopeOf(domain);

  function nextId(prefix: string): string {
    counter.current += 1;
    return `${prefix}-${counter.current}`;
  }

  useEffect(() => {
    let cancel = false;
    void (async () => {
      try {
        const list = await apiGet<readonly { conversationId: string }[]>(
          `${base}/conversations?assistant=${assistantKey}`,
        );
        const first = list[0];
        if (cancel || first === undefined) return;
        conversationIdRef.current = first.conversationId;
        setConversationId(first.conversationId);
        const messages = await apiGet<readonly StoredMessage[]>(
          `${base}/conversations/${first.conversationId}/messages`,
        );
        if (!cancel) setHistory(messages);
      } catch {
        // Missing history is not a fabricated answer.
      }
    })();
    return () => {
      cancel = true;
    };
  }, [base, assistantKey]);

  // A new question scrolls to the end; a new answer scrolls to ITS top — inside the room only.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const last = items[items.length - 1];
    const target =
      last?.kind === 'answer'
        ? scroller.querySelector<HTMLElement>(`[data-item="${last.id}"]`)
        : null;
    if (target) scrollWithin(scroller, target, 'smooth');
    else scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' });
  }, [items, history]);

  /** Replaces a still-waiting card; a card the user stopped stays stopped. */
  function settle(id: string, next: Settled) {
    setItems((current) =>
      current.map((item) =>
        item.id === id && item.kind === 'analyzing' ? ({ ...next, id } as Item) : item,
      ),
    );
  }

  async function forget(messageId: string) {
    if (conversationId === null) return;
    const receipt = await apiDelete<{ scopeLabel?: string }>(`${base}/messages/${messageId}`);
    setForgetNote(
      receipt.scopeLabel ??
        'متن همین پیام و پاسخ همان نوبت پاک می‌شود. مبلغ و سند مالی حفظ می‌شود.',
    );
    // Refresh what is already shown; this session's turns stay in the stream below.
    const fresh = await apiGet<readonly StoredMessage[]>(
      `${base}/conversations/${conversationId}/messages`,
    );
    const byId = new Map(fresh.map((message) => [message.messageId, message]));
    setHistory((current) => current.flatMap((message) => byId.get(message.messageId) ?? []));
  }

  async function remember(question: string, turnId: string) {
    try {
      let id = conversationIdRef.current ?? conversationId;
      if (id === null) {
        const opened = await apiPost<{ conversationId: string }>(`${base}/conversations`, {
          assistantKey,
          title: question.slice(0, 80),
        });
        id = opened.conversationId;
        conversationIdRef.current = id;
        setConversationId(id);
      }
      await apiPost(`${base}/conversations/${id}/messages`, { body: question });
      await apiPost(`${base}/conversations/${id}/turns`, { turnId });
    } catch (err) {
      console.warn('[yar-room] Failed to remember conversation turn:', err);
    }
  }

  async function ask(raw: string) {
    const question = raw.trim();
    if (question === '' || provider.kind !== 'live' || busy) return;
    const cardId = nextId('card');
    setItems((current) => [
      ...current,
      { id: nextId('user'), kind: 'user', text: question },
      { id: cardId, kind: 'analyzing', stage: 'quote' },
    ]);
    setText('');
    try {
      const quote = await provider.createQuote({ question, tool, domain });
      if (!quote.dataSufficient) {
        settle(cardId, {
          kind: 'answer',
          message: yarNotice(cardId, domain, quote.message ?? 'داده کافی موجود نیست'),
        });
        return;
      }
      // Standard workflow answers run straight away; workflow > 20,000 tokens requires confirmation
      if (!quote.confirmationRequired) {
        if (stopped.current.has(cardId)) return;
        setItems((current) =>
          current.map((item) =>
            item.id === cardId && item.kind === 'analyzing' ? { ...item, stage: 'run' } : item,
          ),
        );
        await execute(cardId, quote.quoteId, question);
        return;
      }
      let balance: number | null = null;
      let tokenBalance: number | null = null;
      try {
        const wallet = await provider.getBalance();
        if (wallet.rawTokens?.available) {
          tokenBalance = Number(wallet.rawTokens.available);
        }
        balance = wallet.available.toman === null ? null : Number(wallet.available.toman);
      } catch {
        // The ceiling is still shown; the balance line is left out.
      }
      const estimatedTokens =
        quote.estimatedTotalTokens ??
        ((quote.reserveInputTokens ?? 0) + (quote.reserveOutputTokens ?? 0));
      const estimate =
        estimatedTokens > 0
          ? estimatedTokens
          : quote.amountToman === null
            ? 0
            : Number(quote.amountToman);
      settle(cardId, {
        kind: 'confirm',
        question,
        message: {
          id: cardId,
          role: 'assistant',
          kind: 'confirm-heavy',
          estimate,
          balance: tokenBalance ?? balance,
          unit: 'توکن',
          quoteId: quote.quoteId,
          reason: 'costing',
          status: 'pending',
          note:
            'برآورد مجموع پردازش این پرسش بیش از ۲۰٬۰۰۰ توکن است. تا تأیید «اجرا» اعتباری رزرو نمی‌شود و پس از پایان، فقط مصرف قطعی کسر می‌گردد.',
          replay: {
            request: { type: 'prompt', text: question, selected: [scope] },
            userText: question,
            selected: [scope],
            period: 'this-month',
          },
        },
      });
    } catch (error) {
      settle(cardId, { kind: 'error', text: failureText(error), question });
    }
  }

  function decide(item: Extract<Item, { kind: 'confirm' }>, status: 'confirmed' | 'cancelled') {
    setItems((current) =>
      current.map((entry) =>
        entry.id === item.id && entry.kind === 'confirm'
          ? { ...entry, message: { ...entry.message, status } }
          : entry,
      ),
    );
  }

  async function run(item: Extract<Item, { kind: 'confirm' }>) {
    const quoteId = item.message.quoteId;
    if (provider.kind !== 'live' || quoteId === undefined || busy) return;
    decide(item, 'confirmed');
    const cardId = nextId('card');
    setItems((current) => [...current, { id: cardId, kind: 'analyzing', stage: 'run' }]);
    await execute(cardId, quoteId, item.question);
  }

  /** Runs a quote into the waiting card `cardId`. */
  async function execute(cardId: string, quoteId: string, question: string) {
    if (provider.kind !== 'live') return;
    try {
      const turn = await provider.createRun({ quoteId, idempotencyKey: crypto.randomUUID() });
      settle(cardId, { kind: 'answer', message: yarAnswer(cardId, domain, turn, question) });
      void remember(question, turn.turnId);
    } catch (error) {
      settle(cardId, { kind: 'error', text: failureText(error), question });
    }
  }

  function stop(message: AnalyzingMessage) {
    stopped.current.add(message.id);
    setItems((current) =>
      current.map((item) =>
        item.id === message.id && item.kind === 'analyzing'
          ? { id: item.id, kind: 'stopped' }
          : item,
      ),
    );
  }

  const visibleHistory = history.filter((message) => message.body);
  const empty = visibleHistory.length === 0 && items.length === 0;

  return (
    <YarRoomShell
      disabled={false}
      busy={busy}
      note={forgetNote}
      text={text}
      onText={setText}
      onSend={() => void ask(text)}
      yarName={yarName}
      scrollerRef={scrollerRef}
      {...(placeholder !== undefined ? { placeholder } : {})}
    >
      {empty ? (
        <YarEmpty
          yarName={yarName}
          {...(avatar !== undefined ? { avatar } : {})}
          suggestions={suggestions ?? []}
          onPick={(question) => void ask(question)}
          disabled={busy}
        />
      ) : (
        <div className="ai-stream" aria-label={`گفتگو با ${yarName}`}>
          {visibleHistory.map((message) =>
            message.role === 'user' ? (
              <div key={message.messageId} className="ai-msg ai-msg--user">
                <div className="ai-bubble">{message.body}</div>
                <button
                  type="button"
                  className="yar-forget-btn"
                  onClick={() => void forget(message.messageId)}
                  title="پاک کردن پیام و حافظه مشتق"
                >
                  فراموش شود
                </button>
              </div>
            ) : (
              <div key={message.messageId} className="ai-msg ai-msg--assistant">
                <div className="sk-card ai-answer">
                  <TextBlock parts={[message.body ?? '']} />
                </div>
              </div>
            ),
          )}
          {items.map((item) => (
            <div
              key={item.id}
              data-item={item.id}
              className={`ai-msg ai-msg--${item.kind === 'user' ? 'user' : 'assistant'}`}
            >
              {renderItem(item)}
            </div>
          ))}
        </div>
      )}
    </YarRoomShell>
  );

  function renderItem(item: Item): ReactNode {
    switch (item.kind) {
      case 'user':
        return <div className="ai-bubble">{item.text}</div>;
      case 'analyzing':
        return (
          <AnalyzingCard
            message={{ id: item.id, role: 'assistant', kind: 'analyzing', modules: [scope] }}
            // A confirmed run is already reserved on the server: only the quote wait can stop.
            onStop={item.stage === 'quote' ? stop : undefined}
          />
        );
      case 'stopped':
        return <StoppedNote />;
      case 'confirm':
        return (
          <ConfirmHeavyCard
            message={item.message}
            onConfirm={() => void run(item)}
            onCancel={() => decide(item, 'cancelled')}
            busy={busy}
          />
        );
      case 'answer':
        return (
          <AnswerView
            message={item.message}
            onDrill={NO_OP}
            onFocusRecord={NO_OP}
            isLatest={false}
            busy={busy}
          />
        );
      case 'error':
        return (
          <div className="sk-card ai-state ai-error" role="alert">
            <div className="ai-error__head">
              <span aria-hidden="true">
                <Icon name="shield" size="md" />
              </span>
              <span>{item.text}</span>
            </div>
            <div className="ai-error__detail">متن پرسش شما حفظ شده است.</div>
            <div className="ai-error__actions">
              <button
                type="button"
                className="sk-btn sk-btn--primary sk-btn--sm"
                disabled={busy}
                onClick={() => void ask(item.question)}
              >
                تلاش دوباره
              </button>
              <button
                type="button"
                className="sk-btn sk-btn--ghost sk-btn--sm"
                onClick={() => setText(item.question)}
              >
                ویرایش پرسش
              </button>
            </div>
          </div>
        );
    }
  }
}

/** The empty conversation: the character, one line on what it answers, ready questions. */
export function YarEmpty({
  yarName,
  avatar,
  suggestions = [],
  onPick,
  disabled = false,
}: {
  readonly yarName: string;
  readonly avatar?: string;
  readonly suggestions?: readonly string[];
  readonly onPick?: (question: string) => void;
  readonly disabled?: boolean;
}) {
  return (
    <div className="ai-empty yar-empty">
      <div className="ai-empty__hero">
        {avatar ? (
          <img className="yar-empty__avatar" src={avatar} alt="" />
        ) : (
          <span className="ai-empty__mark" aria-hidden="true">
            <Icon name="star" size="lg" />
          </span>
        )}
        <h2 className="ai-empty__title">از {yarName} بپرسید</h2>
        <p className="ai-empty__text">
          پاسخ کامل از داده‌های ثبت‌شدهٔ همین ماژول ساخته می‌شود. هزینه بر اساس مصرف واقعی کسر
          می‌شود و فقط برای پاسخ بلندتر از ۱۰۰۰ توکن، پیش از اجرا تأیید گرفته می‌شود.
        </p>
      </div>
      {onPick && suggestions.length > 0 ? (
        <div className="ai-suggest yar-suggest" aria-label={`پرسش‌های آماده ${yarName}`}>
          {suggestions.map((question) => (
            <button
              key={question}
              type="button"
              className="sk-card ai-suggest__card"
              disabled={disabled}
              onClick={() => onPick(question)}
            >
              <span className="ai-suggest__tag">پرسش آماده</span>
              <span className="ai-suggest__text">{question}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** The room frame shared by every yar: scrollable conversation above the composer. */
export function YarRoomShell({
  children,
  disabled,
  busy = false,
  note,
  text = '',
  onText,
  onSend,
  yarName,
  placeholder,
  scrollerRef,
}: {
  readonly children: ReactNode;
  readonly disabled: boolean;
  /** An answer is being prepared: the input stays editable, sending waits. */
  readonly busy?: boolean;
  readonly note: string | null;
  readonly text?: string;
  readonly onText?: (value: string) => void;
  readonly onSend?: () => void;
  readonly yarName: string;
  readonly placeholder?: string;
  readonly scrollerRef?: RefObject<HTMLDivElement | null>;
}) {
  const off = disabled || onSend === undefined;
  const canSend = !off && !busy && text.trim() !== '';
  return (
    <div className="yar-page__body">
      <div className="yar-room">
        {note ? <p className="yar-note yar-room__note">{note}</p> : null}
        <div ref={scrollerRef} className="yar-room__body" role="log" aria-live="polite">
          {children}
        </div>
        <form
          className="ai-composer"
          data-disabled={off}
          onSubmit={(event) => {
            event.preventDefault();
            if (canSend) onSend?.();
          }}
        >
          <textarea
            className="sk-textarea ai-composer__input"
            rows={2}
            disabled={off}
            aria-label={`پرسش از ${yarName}`}
            placeholder={placeholder ?? `از ${yarName} بپرسید…`}
            value={text}
            onChange={(event) => onText?.(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
              event.preventDefault();
              if (canSend) onSend?.();
            }}
          />
          <div className="ai-composer__bar">
            <span className="ai-composer__estimate">
              هزینه بر اساس مصرف واقعی کسر می‌شود؛ پاسخ بلندتر از ۱۰۰۰ توکن پیش از اجرا تأیید
              می‌خواهد.
            </span>
            <V2Button
              type="submit"
              variant="primary"
              className="ai-composer__send"
              disabled={!canSend}
            >
              ارسال
            </V2Button>
          </div>
        </form>
      </div>
    </div>
  );
}
